import { cn } from '@client/lib';
import {
  Button,
  Calendar,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@design-system';
import {
  endOfDay,
  format,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfYear,
  subDays,
  subMonths,
} from 'date-fns';
import { CalendarIcon, X } from 'lucide-react';
import { useMemo, useState } from 'react';

interface DateRange {
  from: Date;
  to: Date;
}

interface DateRangeFilterProps {
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
}

const DATE_INPUT_FORMAT = 'yyyy-MM-dd';

type PresetKey =
  | 'today'
  | 'last7days'
  | 'last4weeks'
  | 'last6months'
  | 'last12months'
  | 'monthToDate'
  | 'quarterToDate'
  | 'yearToDate';

interface Preset {
  key: PresetKey;
  label: string;
  getRange: () => DateRange;
}

const PRESETS: Preset[] = [
  {
    key: 'today',
    label: 'Today',
    getRange: () => ({ from: startOfDay(new Date()), to: endOfDay(new Date()) }),
  },
  {
    key: 'last7days',
    label: 'Last 7 days',
    getRange: () => ({ from: startOfDay(subDays(new Date(), 6)), to: endOfDay(new Date()) }),
  },
  {
    key: 'last4weeks',
    label: 'Last 4 weeks',
    getRange: () => ({ from: startOfDay(subDays(new Date(), 27)), to: endOfDay(new Date()) }),
  },
  {
    key: 'last6months',
    label: 'Last 6 months',
    getRange: () => ({ from: startOfDay(subMonths(new Date(), 6)), to: endOfDay(new Date()) }),
  },
  {
    key: 'last12months',
    label: 'Last 12 months',
    getRange: () => ({ from: startOfDay(subMonths(new Date(), 12)), to: endOfDay(new Date()) }),
  },
  {
    key: 'monthToDate',
    label: 'Month to date',
    getRange: () => ({ from: startOfMonth(new Date()), to: endOfDay(new Date()) }),
  },
  {
    key: 'quarterToDate',
    label: 'Quarter to date',
    getRange: () => ({ from: startOfQuarter(new Date()), to: endOfDay(new Date()) }),
  },
  {
    key: 'yearToDate',
    label: 'Year to date',
    getRange: () => ({ from: startOfYear(new Date()), to: endOfDay(new Date()) }),
  },
];

function getActivePreset(range: DateRange | undefined): PresetKey | null {
  if (!range) return null;

  for (const preset of PRESETS) {
    const presetRange = preset.getRange();
    if (
      startOfDay(range.from).getTime() === startOfDay(presetRange.from).getTime() &&
      startOfDay(range.to).getTime() === startOfDay(presetRange.to).getTime()
    ) {
      return preset.key;
    }
  }
  return null;
}

function formatTriggerLabel(range: DateRange): string {
  return `${format(range.from, 'MMM d, yyyy')} - ${format(range.to, 'MMM d, yyyy')}`;
}

interface UseDateRangeFilterDraftOptions {
  dateRange: DateRange | undefined;
  onDateRangeChange: (range: DateRange | undefined) => void;
  onClose: () => void;
}

function useDateRangeFilterDraft({
  dateRange,
  onDateRangeChange,
  onClose,
}: UseDateRangeFilterDraftOptions) {
  const [draftRange, setDraftRange] = useState<DateRange | undefined>(dateRange);
  const [startInput, setStartInput] = useState(
    dateRange ? format(dateRange.from, DATE_INPUT_FORMAT) : '',
  );
  const [endInput, setEndInput] = useState(
    dateRange ? format(dateRange.to, DATE_INPUT_FORMAT) : '',
  );
  const [activePreset, setActivePreset] = useState<PresetKey | null>(() =>
    getActivePreset(dateRange),
  );

  function syncFromCommitted(range: DateRange | undefined) {
    setDraftRange(range);
    setStartInput(range ? format(range.from, DATE_INPUT_FORMAT) : '');
    setEndInput(range ? format(range.to, DATE_INPUT_FORMAT) : '');
    setActivePreset(getActivePreset(range));
  }

  function handlePresetClick(preset: Preset) {
    const range = preset.getRange();
    setDraftRange(range);
    setStartInput(format(range.from, DATE_INPUT_FORMAT));
    setEndInput(format(range.to, DATE_INPUT_FORMAT));
    setActivePreset(preset.key);
  }

  function handleCalendarSelect(selected: { from?: Date; to?: Date } | undefined) {
    if (!selected) return;

    const from = selected.from;
    const to = selected.to;

    if (from && to) {
      const range = { from: startOfDay(from), to: endOfDay(to) };
      setDraftRange(range);
      setStartInput(format(range.from, DATE_INPUT_FORMAT));
      setEndInput(format(range.to, DATE_INPUT_FORMAT));
    } else if (from) {
      setDraftRange({ from: startOfDay(from), to: endOfDay(from) });
      setStartInput(format(from, DATE_INPUT_FORMAT));
      setEndInput('');
    }
    setActivePreset(null);
  }

  function handleStartInputChange(value: string) {
    setStartInput(value);
    if (!value) return;
    const parsed = new Date(`${value}T00:00:00`);
    if (isNaN(parsed.getTime())) return;

    const to = draftRange?.to && parsed <= draftRange.to ? draftRange.to : endOfDay(parsed);
    setDraftRange({ from: startOfDay(parsed), to });
    setActivePreset(null);
  }

  function handleEndInputChange(value: string) {
    setEndInput(value);
    if (!value) return;
    const parsed = new Date(`${value}T00:00:00`);
    if (isNaN(parsed.getTime())) return;

    const from =
      draftRange?.from && parsed >= draftRange.from ? draftRange.from : startOfDay(parsed);
    setDraftRange({ from, to: endOfDay(parsed) });
    setActivePreset(null);
  }

  function handleApply() {
    onDateRangeChange(draftRange);
    onClose();
  }

  function handleClear() {
    setDraftRange(undefined);
    setStartInput('');
    setEndInput('');
    setActivePreset(null);
    onDateRangeChange(undefined);
    onClose();
  }

  const calendarSelected = useMemo(() => {
    if (!draftRange) return undefined;
    return { from: draftRange.from, to: draftRange.to };
  }, [draftRange]);

  const defaultMonth = useMemo(() => {
    if (draftRange?.from) {
      const previousMonth = new Date(draftRange.from);
      previousMonth.setMonth(previousMonth.getMonth() - 1);
      return previousMonth;
    }
    const now = new Date();
    now.setMonth(now.getMonth() - 1);
    return now;
  }, [draftRange]);

  return {
    draftRange,
    startInput,
    endInput,
    activePreset,
    calendarSelected,
    defaultMonth,
    syncFromCommitted,
    handlePresetClick,
    handleCalendarSelect,
    handleStartInputChange,
    handleEndInputChange,
    handleApply,
    handleClear,
  };
}

// ─────────────────────────────────────────────────────────────
// DATE PICKER INPUT
// ─────────────────────────────────────────────────────────────

interface DatePickerInputProps {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  max?: string;
  placeholder?: string;
}

function DatePickerInput({
  value,
  onChange,
  min,
  max,
  placeholder = 'Pick a date',
}: DatePickerInputProps) {
  const [open, setOpen] = useState(false);

  const selected = value ? new Date(`${value}T00:00:00`) : undefined;
  const displayDate = selected ? format(selected, 'MMM d, yyyy') : null;

  const minDate = min ? new Date(`${min}T00:00:00`) : undefined;
  const maxDate = max ? new Date(`${max}T00:00:00`) : undefined;

  function isDisabled(date: Date): boolean {
    if (minDate && date < minDate) return true;
    if (maxDate && date > maxDate) return true;
    return false;
  }

  function handleSelect(date: Date | undefined) {
    onChange(date ? format(date, DATE_INPUT_FORMAT) : '');
    setOpen(false);
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger>
        <Button
          variant="outline"
          size="sm"
          className={cn(!displayDate && 'text-muted-foreground')}
        >
          <CalendarIcon className="mr-1 h-3.5 w-3.5 shrink-0" />
          {displayDate ?? placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
      >
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          defaultMonth={selected}
          disabled={isDisabled}
        />
      </PopoverContent>
    </Popover>
  );
}

// ─────────────────────────────────────────────────────────────
// SHARED CONTENT
// ─────────────────────────────────────────────────────────────

interface DateRangeFilterContentProps {
  startInput: string;
  endInput: string;
  activePreset: PresetKey | null;
  calendarSelected: { from: Date; to: Date } | undefined;
  defaultMonth: Date;
  numberOfMonths: 1 | 2;
  layout: 'side-by-side' | 'stacked';
  onPresetClick: (preset: Preset) => void;
  onCalendarSelect: (selected: { from?: Date; to?: Date } | undefined) => void;
  onStartInputChange: (value: string) => void;
  onEndInputChange: (value: string) => void;
  onApply: () => void;
  onClear: () => void;
}

function DateRangeFilterContent({
  startInput,
  endInput,
  activePreset,
  calendarSelected,
  defaultMonth,
  numberOfMonths,
  layout,
  onPresetClick,
  onCalendarSelect,
  onStartInputChange,
  onEndInputChange,
  onApply,
  onClear,
}: DateRangeFilterContentProps) {
  const presets = (
    <div
      className={cn(layout === 'side-by-side' ? 'flex flex-col gap-1' : 'flex flex-wrap gap-1.5')}
    >
      {PRESETS.map(preset => (
        <Button
          key={preset.key}
          variant={layout === 'stacked' ? 'outline' : 'ghost'}
          size="sm"
          onClick={() => onPresetClick(preset)}
          className={cn(
            layout === 'side-by-side' && 'justify-start',
            activePreset === preset.key
              ? 'bg-primary/15 text-primary-dark font-medium'
              : 'text-muted-foreground',
          )}
        >
          {preset.label}
        </Button>
      ))}
    </div>
  );

  const dateInputs = (
    <div className="flex items-center gap-4 px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground text-sm">Start</span>
        <DatePickerInput
          value={startInput}
          max={endInput || format(new Date(), DATE_INPUT_FORMAT)}
          onChange={onStartInputChange}
          placeholder="mm/dd/yyyy"
        />
      </div>
      <div className="flex items-center gap-2">
        <span className="text-muted-foreground text-sm">End</span>
        <DatePickerInput
          value={endInput}
          min={startInput}
          max={format(new Date(), DATE_INPUT_FORMAT)}
          onChange={onEndInputChange}
          placeholder="mm/dd/yyyy"
        />
      </div>
    </div>
  );

  const actions = (
    <div className="flex items-center justify-end gap-2 px-4 py-3">
      <Button
        variant="outline"
        size="sm"
        onClick={onClear}
      >
        Clear
      </Button>
      <Button
        size="sm"
        onClick={onApply}
      >
        Apply
      </Button>
    </div>
  );

  if (layout === 'stacked') {
    return (
      <div className="flex flex-col divide-y px-4">
        <div className="py-3">{presets}</div>
        <div className="py-3">{dateInputs}</div>
        <Calendar
          mode="range"
          className="mx-auto w-full max-w-xs"
          selected={calendarSelected}
          onSelect={onCalendarSelect}
          numberOfMonths={numberOfMonths}
          defaultMonth={defaultMonth}
          disabled={{ after: new Date() }}
        />
        <div>{actions}</div>
      </div>
    );
  }

  return (
    <div className="flex">
      <div className="border-r p-3 pr-2">{presets}</div>
      <div className="flex flex-col">
        <div className="border-b">{dateInputs}</div>
        <Calendar
          mode="range"
          selected={calendarSelected}
          onSelect={onCalendarSelect}
          numberOfMonths={numberOfMonths}
          defaultMonth={defaultMonth}
          disabled={{ after: new Date() }}
        />
        <div className="border-t">{actions}</div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// DESKTOP (Popover)
// ─────────────────────────────────────────────────────────────

function DateRangeFilter({ dateRange, onDateRangeChange }: DateRangeFilterProps) {
  const [open, setOpen] = useState(false);
  const hasActiveFilter = Boolean(dateRange);

  const draft = useDateRangeFilterDraft({
    dateRange,
    onDateRangeChange,
    onClose: () => setOpen(false),
  });

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      draft.syncFromCommitted(dateRange);
    }
    setOpen(nextOpen);
  }

  function handleClearTrigger(event: React.MouseEvent) {
    event.stopPropagation();
    onDateRangeChange(undefined);
  }

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
    >
      <PopoverTrigger>
        <Button
          variant="outline"
          className={cn(hasActiveFilter && 'bg-primary/10 border-primary')}
        >
          <CalendarIcon />
          {hasActiveFilter && dateRange ? (
            <>
              <span>{formatTriggerLabel(dateRange)}</span>
              <button
                onClick={handleClearTrigger}
                className="text-primary hover:opacity-70"
                aria-label="Clear date filter"
                type="button"
              >
                <X />
              </button>
            </>
          ) : (
            <span>Date range</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-auto border p-0 shadow-xl"
      >
        <DateRangeFilterContent
          startInput={draft.startInput}
          endInput={draft.endInput}
          activePreset={draft.activePreset}
          calendarSelected={draft.calendarSelected}
          defaultMonth={draft.defaultMonth}
          numberOfMonths={2}
          layout="side-by-side"
          onPresetClick={draft.handlePresetClick}
          onCalendarSelect={draft.handleCalendarSelect}
          onStartInputChange={draft.handleStartInputChange}
          onEndInputChange={draft.handleEndInputChange}
          onApply={draft.handleApply}
          onClear={draft.handleClear}
        />
      </PopoverContent>
    </Popover>
  );
}

// ─────────────────────────────────────────────────────────────
// MOBILE (Drawer)
// ─────────────────────────────────────────────────────────────

function DateRangeFilterDrawer({ dateRange, onDateRangeChange }: DateRangeFilterProps) {
  const [open, setOpen] = useState(false);
  const hasActiveFilter = Boolean(dateRange);

  const draft = useDateRangeFilterDraft({
    dateRange,
    onDateRangeChange,
    onClose: () => setOpen(false),
  });

  function handleOpenChange(nextOpen: boolean) {
    if (nextOpen) {
      draft.syncFromCommitted(dateRange);
    }
    setOpen(nextOpen);
  }

  return (
    <Drawer
      open={open}
      onOpenChange={handleOpenChange}
    >
      <DrawerTrigger>
        <Button
          variant="outline"
          size="icon"
          className={cn(
            'h-9 w-9 border-[#e8e0ff] text-[#7755CC]',
            hasActiveFilter && 'bg-primary/10 border-primary',
          )}
          aria-label="Date filter"
        >
          <CalendarIcon className="h-4 w-4" />
        </Button>
      </DrawerTrigger>
      <DrawerContent>
        <DrawerHeader>
          <DrawerTitle>Filter by date</DrawerTitle>
        </DrawerHeader>
        <div className="overflow-y-auto">
          <DateRangeFilterContent
            startInput={draft.startInput}
            endInput={draft.endInput}
            activePreset={draft.activePreset}
            calendarSelected={draft.calendarSelected}
            defaultMonth={draft.defaultMonth}
            numberOfMonths={1}
            layout="stacked"
            onPresetClick={draft.handlePresetClick}
            onCalendarSelect={draft.handleCalendarSelect}
            onStartInputChange={draft.handleStartInputChange}
            onEndInputChange={draft.handleEndInputChange}
            onApply={draft.handleApply}
            onClear={draft.handleClear}
          />
        </div>
        <DrawerFooter className="sr-only">
          <DrawerClose asChild>
            <Button variant="outline">Close</Button>
          </DrawerClose>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}

export { DateRangeFilter, DateRangeFilterDrawer };
export type { DateRange };

